import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  Location,
  PolicyRecommendation,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { getCountryName } from '../../Utils';
import Localized from '../landing/Localized';
import { primaryColor2 } from '../Utils';
import {
  ContentContainer,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

const query = gql`
  query HighPotentialProductsSideBar($id: ID!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $id) {
      latestEciRank
      policyRecommendation
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id'],
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    latestEciRank: CountryProfile['latestEciRank'],
    policyRecommendation: CountryProfile['policyRecommendation'],

  };
}

interface Variables {
  id: string;
  productClass: ProductClass;
}

const TermContainer = styled.span`
  color: ${primaryColor2};
`;
interface TermProps {
  text: string;
}
const Term = ({text}: TermProps) => (
  <TermContainer>{text}</TermContainer>
);

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  countryId: string;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

export default (props: Props) => {
  const {
    getFluentString, onNextSlideClick, fluentBundle,
    countryId, onPrevSlideClick, isHelpActive, helpTooltipIndex,
  } = props;

  const variables: Variables = {
    id: countryId,
    productClass: ProductClass.SITC,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = null;
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {
    const {location, countryProfile} = data;

    if (location === null || countryProfile === null || countryProfile.policyRecommendation === null)  {
      out = null;
    } else {
      const {shortName, nameAbbr, thePrefix} = location;
      const {latestEciRank} = countryProfile;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      if (latestEciRank === null) {
        out = null;
      } else if (countryProfile.policyRecommendation === PolicyRecommendation.TechFrontier) {
        const paragraph = getFluentString('early-exit-sidebar-text', {
          'country-name-first': namePrimary,
          'country-name': nameSecondary,
        });
        out = (
          <>
            <IntroText>
              {paragraph}
            </IntroText>
          </>
        );
      } else {
        const item1Args = {
          distanceTerm: (
            <Term
              text={getFluentString('growth-opportunities-intro-text-distance-term')}
            />
          ),
        };
        const item2Args = {
          complexityTerm: (
            <Term
              text={getFluentString('growth-opportunities-intro-text-complexity-term')}
            />
          ),
        };
        const item3Args = {
          opportunityGainTerm: (
            <Term
              text={getFluentString('growth-opportunities-intro-text-opportunity-gain-term')}
            />
          ),
        };
        out = (
          <>
            <IntroText>
              {getFluentString('growth-opportunities-intro-text-para-1')}
            </IntroText>
            <ul>
              <Localized
                bundle={fluentBundle}
                messageId='growth-opportunities-intro-text-item-1'
                messageArgs={item1Args}
              >
                <IntroText as='li'/>
              </Localized>
              <Localized
                bundle={fluentBundle}
                messageId='growth-opportunities-intro-text-item-2'
                messageArgs={item2Args}
              >
                <IntroText as='li'/>
              </Localized>
              <Localized
                bundle={fluentBundle}
                messageId='growth-opportunities-intro-text-item-3'
                messageArgs={item3Args}
              >
                <IntroText as='li'/>
              </Localized>
            </ul>
          </>
        );
      }
    }
  } else {
    out = null;
  }

  return (
    <Slide>
      <SlideAdvancer
        onClick={onPrevSlideClick}
        direction={Direction.Previous}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-diversification-strategic-approach')}
      </SlideAdvancer>
      <ContentContainer>
        <Title>{getFluentString('section-nav-slide-growth-opportunities')}</Title>
        {out}
      </ContentContainer>
      <SlideAdvancer
        onClick={onNextSlideClick}
        direction={Direction.Next}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-high-potential-products')}
      </SlideAdvancer>
    </Slide>
  );

};
